import React from 'react';
import cx from 'classnames';

/**
 * @typedef {ButtonGroupIconOption}
 * @property {IconComponent} Icon an icon component
 * @property {string} value the value for this option
 */
interface ButtonGroupIconOption {
    Icon: IconComponent;
    value: string;
}

/**
 * @typedef {ButtonGroupIconProps}
 * @property {function} handleChange callback to updated the currently selected option
 * @property {ButtonGroupIconOption[]} options list of available options
 * @property {string} selectedValue currently selected option
 */
interface ButtonGroupIconProps {
    disabled?: boolean;
    handleChange(value: string): void;
    options: ButtonGroupIconOption[];
    selectedValue: string;
}

/**
 * Displays a row of buttons, out of which only one can be pressed. The buttons are rendered with
 * icons, rather than text labels. State management is left to the parent component.
 * @param props {ButtonGroupIconProps}
 */
export const ButtonGroupIcon = ({
    disabled = false,
    handleChange,
    options,
    selectedValue,
}: ButtonGroupIconProps): JSX.Element => (
    <div className="inline-block w-auto">
        <div className="flex border border-tertiary-200 rounded-md">
            {options.map(
                ({ Icon, value }: ButtonGroupIconOption): JSX.Element => {
                    const isActive = value === selectedValue;
                    const fillColor =
                        isActive && !disabled ? 'tertiary-200' : 'primary-500';

                    return (
                        <button
                            className={cx({
                                'cursor-default': disabled,
                            })}
                            key={value}
                            type="button"
                            onClick={(): void => handleChange(value)}
                            aria-pressed={isActive}
                            aria-label={selectedValue}
                        >
                            <div
                                className={cx('px-5 py-1 rounded-md', {
                                    'bg-primary-500': isActive && !disabled,
                                    'bg-tertiary-300': isActive && disabled,
                                })}
                            >
                                <Icon fillColor={fillColor} />
                            </div>
                        </button>
                    );
                },
            )}
        </div>
    </div>
);
